/**
 * Per-pass GPU timing with EXT_disjoint_timer_query_webgl2 (when the browser exposes it).
 * Results arrive a few frames late; values are exponentially smoothed milliseconds.
 */
export class GpuTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private pending: { name: string; q: WebGLQuery }[] = [];
  private active: { name: string; q: WebGLQuery } | null = null;
  readonly ms: Record<string, number> = {};
  enabled = true;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2');
  }

  get available() {
    return !!this.ext;
  }

  begin(name: string) {
    if (!this.ext || !this.enabled || this.active) return;
    const q = this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = { name, q };
  }

  end() {
    if (!this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  poll() {
    if (!this.ext) return;
    const gl = this.gl;
    const disjoint = gl.getParameter(this.ext.GPU_DISJOINT_EXT);
    const keep: typeof this.pending = [];
    for (const p of this.pending) {
      if (gl.getQueryParameter(p.q, gl.QUERY_RESULT_AVAILABLE)) {
        if (!disjoint) {
          const ms = gl.getQueryParameter(p.q, gl.QUERY_RESULT) / 1e6;
          this.ms[p.name] = this.ms[p.name] === undefined ? ms : this.ms[p.name] * 0.85 + ms * 0.15;
        }
        gl.deleteQuery(p.q);
      } else keep.push(p);
    }
    this.pending = keep.length > 64 ? keep.slice(-64) : keep;
  }
}
